import { LayerData, userViewerSettingsAtom } from '@/atoms';
import { colours } from '@/core';
import { PositionGroup2 } from '@/core/iprj';
import { Image } from '@/graphql/graphql';
import { useLayerSettingsMany } from '@/hooks/useLayerSettings';
import { useFrame, useLoader, useThree } from '@react-three/fiber';
import { useAtomValue } from 'jotai';
import { Suspense, useEffect, useMemo, useRef, useState, type Dispatch, type SetStateAction } from 'react';
import * as THREE from 'three';
import { Loader } from './Loader';
import { MinimalHotspot } from './MinimalHotspot';

/**
 * Creates a small checkerboard alpha-grid texture as a fallback
 * when an image URL fails to load.
 */
function createAlphaGridTexture(): THREE.Texture {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;

  const cellSize = 8;
  for (let y = 0; y < size; y += cellSize) {
    for (let x = 0; x < size; x += cellSize) {
      const isLight = (x / cellSize + y / cellSize) % 2 === 0;
      ctx.fillStyle = isLight ? 'rgba(204,204,204,0.6)' : 'rgba(255,255,255,0.6)';
      ctx.fillRect(x, y, cellSize, cellSize);
    }
  }

  const texture = new THREE.CanvasTexture(canvas);
  texture.needsUpdate = true;
  return texture;
}

/**
 * Custom texture loader that uses fetch with credentials.
 * On failure, resolves with an opaque alpha-grid texture instead of throwing.
 */
class CredentialsTextureLoader extends THREE.Loader<THREE.Texture> {
  load(
    url: string,
    onLoad?: (texture: THREE.Texture) => void,
    _onProgress?: (event: ProgressEvent) => void,
    _onError?: (err: unknown) => void,
  ): THREE.Texture {
    const texture = new THREE.Texture();

    fetch(url, { credentials: 'include' })
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.blob();
      })
      .then((blob) => createImageBitmap(blob, { imageOrientation: 'flipY' }))
      .then((bitmap) => {
        texture.image = bitmap;
        texture.needsUpdate = true;
        onLoad?.(texture);
      })
      .catch((err) => {
        console.warn(`[IPRJ2] Failed to load texture: ${url}`, err);
        const fallback = createAlphaGridTexture();
        texture.image = fallback.image;
        texture.needsUpdate = true;
        onLoad?.(texture);
      });

    return texture;
  }
}

export const IPRJ = ({
  setPosition,
  // images,
  imagePositions: positions,
  layers,
  imageId,
  setImageId,
  hoverImageId,
  setHoverImageId,
  position,
}: {
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
  visible?: boolean;
  images: Omit<Image, 'layer'>[] | null;
  imagePositions:
    | (PositionGroup2 & { cameraPosition: THREE.Vector3; renderPosition: THREE.Vector3; display: boolean })[]
    | null;
  layers: LayerData[] | null;
  position: THREE.Vector3;
  imageId: null | string;
  setImageId: Dispatch<SetStateAction<string | null>>;
  hoverImageId: string | null;
  setHoverImageId: Dispatch<SetStateAction<string | null>>;
}) => {
  const canvas = useThree((state) => state.gl.domElement);
  const layerSettings = useLayerSettingsMany(layers?.map((l) => l.id) || []);

  const viewerSettings = useAtomValue(userViewerSettingsAtom);
  const renderImagePOIs = viewerSettings?.showImagePOIs ?? true;

  const imagePosition = positions?.find((p) => p.entries[0].id === imageId);

  useEffect(() => {
    if (!imageId || !imagePosition) return;
    if (
      imagePosition.cameraPosition.x === position.x &&
      imagePosition.cameraPosition.y === position.y &&
      imagePosition.cameraPosition.z === position.z
    )
      return;

    setPosition(imagePosition.cameraPosition.clone());
  }, [imageId, positions, setPosition]);

  if (!positions || positions.length === 0 || !imageId) return null;

  return (
    <>
      <Suspense fallback={<Loader />}>
        {imagePosition ? <IPRJSkybox position={imagePosition} layers={layers} setPosition={setPosition} /> : null}
      </Suspense>
      <group>
        {/* optional: global postprocessing bloom */}
        {/* <EffectComposer multisampling={4}>
          <Bloom
            intensity={0.7}
            luminanceThreshold={0.2}
            luminanceSmoothing={0.9}
            blendFunction={BlendFunction.SCREEN}
          />
        </EffectComposer> */}

        {renderImagePOIs
          ? positions
              .filter((p) => p.display)
              .map((p, i) => (
                <MinimalHotspot
                  key={i}
                  position={p.renderPosition}
                  active={imageId === p.entries[0].id}
                  hovered={hoverImageId === p.entries[0].id}
                  colorDefault={
                    layerSettings?.[p.entries[0].layerId as string]?.imagePointColor || colours.images.default
                  }
                  colorHover={colours.images.hover}
                  colorActive={colours.images.active}
                  onPointerEnter={() => {
                    if (imageId === p.entries[0].id) return;
                    setHoverImageId(p.entries[0].id);
                    canvas.style.cursor = 'pointer';
                  }}
                  onPointerLeave={() => {
                    setHoverImageId(null);
                    canvas.style.cursor = 'auto';
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    setImageId(p.entries[0].id);
                  }}
                />
              ))
          : null}
      </group>
    </>
  );
};

function applyEquirectangularTextureSettings(texture: THREE.Texture, maxAnisotropy: number): void {
  texture.wrapS = THREE.RepeatWrapping;
  texture.repeat.x = -1;
  texture.offset.x = 1;
  texture.colorSpace = THREE.SRGBColorSpace;
  texture.generateMipmaps = true;
  texture.minFilter = THREE.LinearMipmapLinearFilter;
  texture.anisotropy = Math.min(8, maxAnisotropy);
}

function getDirectionVector(headingDeg: number, rollDeg: number, pitchDeg: number): number[] {
  // Convert to radians (negate heading and roll for X,Y)
  const h = (-headingDeg * Math.PI) / 180;
  const r = (-rollDeg * Math.PI) / 180;
  const p = (pitchDeg * Math.PI) / 180;

  const ch = Math.cos(h),
    sh = Math.sin(h);
  const cp = Math.cos(p),
    sp = Math.sin(p);
  const cr = Math.cos(r),
    sr = Math.sin(r);

  // X and Y from HRP rotation of [0,0,1]
  const x = ch * sp + sh * sr * cp;
  const y = sh * sp - ch * sr * cp;

  // Z uses original (unnegated) roll
  const r_original = (rollDeg * Math.PI) / 180;
  const z = Math.sin(p) * Math.cos(r_original);

  return [x, y, z];
}

function quatFromHRP(hrp: [number, number, number], rotationOrder: 1 | 2 | 3 | 4 | 6): THREE.Quaternion {
  const [hDeg, rDeg, pDeg] = hrp;
  const h = THREE.MathUtils.degToRad(hDeg);
  const r = THREE.MathUtils.degToRad(rDeg);
  const p = THREE.MathUtils.degToRad(pDeg);

  // per spec: Heading = rot around Z, Roll = rot around X, Pitch = rot around Y
  const H = new THREE.Matrix4().makeRotationZ(h);
  const R = new THREE.Matrix4().makeRotationX(r);
  const P = new THREE.Matrix4().makeRotationY(p);

  // multiply in the exact order from the .iprj file (left→right)
  // 1 – Heading * Pitch * Roll
  // 2 – Heading * Roll * Pitch
  // 3 – Roll * Pitch * Heading
  // 4 – Pitch * Roll * Heading
  const m = new THREE.Matrix4().identity();
  switch (rotationOrder) {
    case 1:
      m.multiply(H).multiply(P).multiply(R);
      break;
    case 2:
      m.multiply(H).multiply(R).multiply(P);
      break;
    case 3:
      m.multiply(R).multiply(P).multiply(H);
      break;
    case 4:
      m.multiply(P).multiply(R).multiply(H);
      break;
    default:
      throw new Error('Invalid rotation order: ' + rotationOrder);
  }

  return new THREE.Quaternion().setFromRotationMatrix(m);
}

const IPRJSkybox = ({
  position,
  layers,
}: {
  position: PositionGroup2;
  layers: LayerData[] | null;
  setPosition: Dispatch<SetStateAction<THREE.Vector3>>;
}) => {
  const camera = useThree((state) => state.camera);
  const gl = useThree((state) => state.gl);

  const layer = useMemo(() => {
    if (!layers) return null;
    return layers.find((l) => l.id === position.entries[0].layerId) || null;
  }, [position, layers]);

  useFrame(() => {
    if (groupRef.current) {
      groupRef.current.position.copy(camera.position);
    }
  });

  // Determine which images to load initially (small if available, otherwise large/original)
  const initialUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      const filename =
        image.hasCompressedVariants && image.smallFilename
          ? image.smallFilename
          : image.largeFilename || image.filename;
      return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
    });
  }, [position.entries, layer]);

  // URLs for large images (only if we have compressed variants)
  const largeUrls = useMemo(() => {
    if (!layer) return [];
    return position.entries.map((image) => {
      if (image.hasCompressedVariants && image.smallFilename) {
        const filename = image.largeFilename || image.filename;
        return `${import.meta.env.VITE_RESOURCES_ENDPOINT}/${layer.root}/${filename}`;
      }
      return null; // No upgrade needed
    });
  }, [position.entries, layer]);

  const textures = useLoader(CredentialsTextureLoader, initialUrls);

  const groupRef = useRef<THREE.Group | null>(null);
  const [upgradedTextures, setUpgradedTextures] = useState<(THREE.Texture | null)[]>([]);

  // Reset upgraded textures when position changes to avoid showing stale images
  useEffect(() => {
    setUpgradedTextures([]);
  }, [position.entries[0].id]);

  // Load large images in background after initial render
  useEffect(() => {
    const needsUpgrade = largeUrls.some((url) => url !== null);
    if (!needsUpgrade) return;

    const loadLargeTextures = async () => {
      const loader = new CredentialsTextureLoader();
      const loaded: (THREE.Texture | null)[] = [];

      for (let i = 0; i < largeUrls.length; i++) {
        const url = largeUrls[i];
        if (!url) {
          loaded.push(null);
          continue;
        }

        try {
          const texture = await new Promise<THREE.Texture>((resolve, reject) => {
            loader.load(url, resolve, undefined, reject);
          });
          loaded.push(texture);
        } catch {
          loaded.push(null);
        }
      }

      setUpgradedTextures(loaded);
    };

    loadLargeTextures();
  }, [largeUrls, gl, position.entries]);

  // Use upgraded texture if available, otherwise use initial texture
  const activeTextures = useMemo(() => {
    return textures.map((tex, i) => upgradedTextures[i] || tex);
  }, [textures, upgradedTextures]);

  useEffect(() => {
    if (!position.entries[0].focalLengthX) {
      const maxAnisotropy = gl.capabilities.getMaxAnisotropy();
      activeTextures.forEach((texture) => applyEquirectangularTextureSettings(texture, maxAnisotropy));
    }
  }, [activeTextures, gl, position.entries]);

  const sphereGeom = useMemo(() => {
    const sphereGeom = new THREE.SphereGeometry(10, 64, 32);
    sphereGeom.rotateX(Math.PI / 2);
    return sphereGeom;
  }, [textures]);

  return (
    <group ref={groupRef}>
      {position.entries.map((entry, i: number) => {
        const k = 100;

        // Common orientation
        const q = new THREE.Quaternion(
          entry.rotationQuaternion[0],
          entry.rotationQuaternion[1],
          entry.rotationQuaternion[2],
          entry.rotationQuaternion[3],
        );

        if (!entry.focalLengthX) {
          return (
            <mesh key={entry.id} quaternion={q} renderOrder={-9999}>
              <primitive object={sphereGeom} />
              <meshBasicMaterial
                map={activeTextures[i]}
                side={THREE.BackSide}
                depthWrite={false}
                depthTest={false}
                toneMapped={false}
                fog={false}
              />
            </mesh>
          );
        }

        // ----- Pinhole (6 images) -----
        // intrinsics (guard against nulls)
        const Wpx = entry.width;
        const Hpx = entry.height;
        const sx = entry.pixelWidth; // meters per pixel
        const sy = entry.pixelHeight; // meters per pixel
        const fx = entry.focalLengthX!; // meters
        const fy = entry.focalLengthY! || entry.focalLengthX!; // meters
        const cx = entry.principalPointX ?? Wpx / 2;
        const cy = entry.principalPointY ?? Hpx / 2;

        // derived sensor size in world units
        const Sx = Wpx * sx * k;
        const Sy = Hpx * sy * k;

        let d, planeWidth, planeHeight, tx, ty, dir, offset;

        let side;
        if (sx && sy) {
          // choose plane distance
          d = ((fx + fy) / 2) * k;

          // plane size at distance d
          planeWidth = (d / (fx * k)) * Sx; // = Sx if d==f*k
          planeHeight = (d / (fy * k)) * Sy;

          // local translation so plane center aligns with principal point
          tx = (cx - Wpx / 2) * sx * k;
          ty = (cy - Hpx / 2) * sy * k;

          // Plane center offset along the camera's local -Z (forward in E57; z<0)
          dir = new THREE.Vector3(0, 0, -1).applyQuaternion(q).normalize();
          side = THREE.FrontSide;
        } else {
          planeWidth = Wpx;
          planeHeight = Hpx;

          tx = cx - Wpx / 2;
          ty = cy - Hpx / 2;

          // Plane center offset along the camera's local Z (forward in Topodot; z>0)
          dir = new THREE.Vector3(0, 0, 1).applyQuaternion(q).normalize();
          side = THREE.BackSide;

          d = (entry.width + entry.height) / 4;

          activeTextures[i].flipY = false;
          // activeTextures[i].needsUpdate = true;
        }
        offset = dir.clone().multiplyScalar(d);

        const translation = new THREE.Vector3(tx, ty, 0).applyQuaternion(q);

        return (
          <mesh key={entry.id} position={offset.toArray()} quaternion={q}>
            <group position={translation}>
              <mesh renderOrder={-9999}>
                <planeGeometry args={[planeWidth, planeHeight]} />
                <meshBasicMaterial
                  map={activeTextures[i]}
                  side={side}
                  depthWrite={false}
                  depthTest={false}
                  toneMapped={false}
                  fog={false}
                />
              </mesh>
            </group>
          </mesh>
        );
      })}
    </group>
  );
};
